import Vue from 'vue';
import FileBrowser from "../../services/api/file-browser";
import {FILE_TYPE, SOURCE} from "../../constants/file-image-types";
import {TABS, API_TABS} from "../../components/modules/file-browser/constants";

import router from '../../router/index'
import {SETTINGS_MODAL_FIELDS_NAME} from "../../components/modules/file-browser/modals/settings-modal/fields";
import {
    resolveDeleteItemMethod,
    resolveDownloadMethod,
    resolveMultipleDeleteMethod,
    resolveSource
} from "../../components/modules/file-browser/method-resolvers";
import {downloadFileLink, clampPage} from "../../components/modules/file-browser/helpers";
import _ from "lodash";
import Requests from "../../services/api/file-browser";
import { prepareProductFileForm, uploadFile, uploadFolderFile, uploadFolderLink } from "../../services/file-uploader";
import {addProcessingProp} from "../../components/modules/file-browser/data-translators";
import {MODALS} from "../../components/modules/file-browser/constants";
import {createModal} from "../../components/modules/file-browser/modals/action-creater";
import {listMapperFactory} from "../../components/modules/file-browser/list-mappers";
import fetchListDTO, {
    fetchListDataBasedActionCreator,
    fetchListRouterBasedActionCreator
} from "../../components/modules/file-browser/dto/fetch-list-dto";
import {EVENTS as PSD_LAYERS_EVENT, psdLayersEventBroker} from "@frontend/group/modules/psd-layers/event-broker";
import {clampGridDensity} from "@frontend/components/modules/file-browser/grid-density";
import {clearPsdImageSegmentationJobs} from '@frontend/components/modules/psd-layers/services/image-segmentation-jobs';
import { enqueueBackgroundUploads } from '../../services/background-uploads/bridge';
import { shouldUseBackgroundUploads } from '../../services/background-uploads/policy';

const CHUNK_SIZE = 5 * 1024 * 1024;

const uploadedFileItem = file => {
    const shareUrl = path => path && !/^https?:\/\//i.test(path)
        ? `${window.location.origin}/share?file=${path}` : path;
    return {
        ...file,
        original_path: file.path,
        path: shareUrl(file.path),
        url: shareUrl(file.path),
        url_shorten: shareUrl(file.path),
        thumbnail_path: shareUrl(file.thumbnail_path),
        destination_name: file.name,
        extension: file.path?.split('.').pop(),
        asset_type: 'file_asset',
        created_at_timestamp: file.created_at,
        is_psd_parsed: false,
        is_fig_parsed: false,
        is_idml_parsed: false,
        percent: 0,
        upload_error: false,
    };
};

const getEditableItemType = (item) => {
    if (!item?.type) {
        return 'item';
    }

    return item.type === FILE_TYPE.FOLDER ? 'folder' : 'file';
};

let pendingUploads = 0;
const startUpload = () => {
    pendingUploads++;
};
const finishUpload = (dispatch, batch) => {
    pendingUploads--;
    if (pendingUploads <= 0) {
        pendingUploads = 0;

        // `settleBatchItem` already refreshed for a batch that brought its own handler, and the
        // router-based refetch would replace that listing with the root.
        if (batch?.onSettled) return;

        dispatch('fetchList').catch(() => {});
    }
};

const createUploadBatch = (total, onSettled = null, data = []) => ({ remaining: total, succeeded: 0, onSettled, data });

const settleBatchItem = (batch, succeeded) => {
    if (!batch) return;
    batch.remaining--;
    if (succeeded) batch.succeeded++;
    if (batch.remaining > 0) return;

    if (batch.succeeded > 0) {
        toastr.success(`${batch.succeeded} ${batch.succeeded > 1 ? 'files' : 'file'} uploaded`);
    }

    if (batch.onSettled) {
        Promise.resolve(batch.onSettled(batch.data)).catch(() => {});
    }
};

// Shared tail of both upload actions: split what the server already has, then queue the rest.
const queueUploads = ({ commit, dispatch }, { data, folderId, fileCount, onSettled, onPrepared }) => {
    const exists = data.filter(({ isExist, uploadId }) => isExist && !uploadId);

    if (exists.length) {
        commit(MUTATIONS.SET_MODAL, createModal(MODALS.FILE_EXISTS, {
            files: exists,
            uploadContext: onPrepared ? { folderId, onPrepared, onSettled } : null,
        }));
    }

    Requests.sendSharedAction({ id: folderId, files: fileCount, action: 'upload' });

    const toUpload = data.filter(({ isExist, uploadId }) => !isExist || uploadId);
    const batch = createUploadBatch(toUpload.length, onSettled, data);

    toUpload.forEach(item => dispatch('uploadFile', { ...item, batch }));

    return toUpload.length;
};

const initState = () => ({
    isListLoading: false,
    items: [],
    progressingItems: [],
    selected: [],
    selectedItemCache: [],
    pagination: {
        pageSize: null,
        pageSizeInvalidFeedback: null,
        pageSizeValidationState: null,
        totalCount: 0,
        folderCount: 0,
        lastPage: 1,
    },
    searchQuery: '',
    modal: {
        type: '',
        payload: null,
    },
    compressSettings: {
        isFetching: false,
        isSaving: false,
        replaceOriginalImage: false,
        data: {
            [SETTINGS_MODAL_FIELDS_NAME.image_compress_max_size_webp]: '',
            [SETTINGS_MODAL_FIELDS_NAME.image_compress_quality]: '',
            [SETTINGS_MODAL_FIELDS_NAME.quality_compress_video]: '',
            [SETTINGS_MODAL_FIELDS_NAME.when_convert_delete_original_file]: 'off'
        },
    },
    isDownloading: false,
    isDuplicates: false,
    isThumbnailGenerating: false,
    stockTags: ['test', 'item', 'icon', 'new'],
    isStockTagsLoading: false,
    breadcrumbs: [],
    isSvgLoading: false,
    showFavorites: false,
    showOnlyFolders: false,
    bynderAvailable: false,
    youtubeAvailable: false,
    bynderTabLabel: null,
    salsifyTabLabel: null,
    salsifyAvailable: false,
    isPublishToBynder: false,
    isBynderDownloading: false,
    initialCompanyId: null,
    hasCompanyMismatch: false,
    gridDensity: 0,
    optimizelyAvailable: false,
})

export const MUTATIONS = {
    SET_ITEMS: 'SET_ITEMS',
    PATCH_UPLOAD_ITEMS: 'PATCH_UPLOAD_ITEMS',
    RECONCILE_ITEMS: 'RECONCILE_ITEMS',
    FAIL_PROGRESSING: 'FAIL_PROGRESSING',
    UPDATE_PROGRESS_PERCENTAGE: 'UPDATE_PROGRESS_PERCENTAGE',
    SET_LIST_LOADING: 'SET_LIST_LOADING',
    SET_TOTAL_COUNT: 'SET_TOTAL_COUNT',
    SET_FOLDER_COUNT: 'SET_FOLDER_COUNT',
    SET_LAST_PAGE: 'SET_LAST_PAGE',
    TOGGLE_SELECTED_ITEMS: 'TOGGLE_SELECTED_ITEMS',
    CLEAR_SELECTION: 'CLEAR_SELECTION',
    SET_MODAL: 'SET_MODAL',
    COMPRESS_SETTINGS: {
        SET_FETCHING: 'COMPRESS_SETTINGS_SET_FETCHING',
        SET_SAVING: 'COMPRESS_SETTINGS_SET_SAVING',
        SET_REPLACE_ORIGINAL_IMAGE: 'COMPRESS_SETTINGS_SET_REPLACE_ORIGINAL_IMAGE',
        SET_DATA: 'COMPRESS_SETTINGS_SET_DATA',
        SET_DATA_KEY: 'COMPRESS_SETTINGS_SET_DATA_KEY'
    },
    SET_DOWNLOADING: 'SET_DOWNLOADING',
    SET_IS_DUPLICATES: 'SET_IS_DUPLICATES',
    SET_THUMBNAIL_GENERATING: 'SET_THUMBNAIL_GENERATING',
    UPDATE_THUMBNAIL_BY_ID: 'UPDATE_THUMBNAIL_BY_ID',
    FINISH_PROGRESSING: 'FINISH_PROGRESSING',
    UPDATE_ITEM_NAME: 'UPDATE_ITEM_NAME',
    UPDATE_ITEM_CUSTOMIZATION: 'UPDATE_ITEM_CUSTOMIZATION',
    SET_STOCK_TAGS: 'SET_STOCK_TAGS',
    SET_STOCK_TAGS_LOADING: 'SET_STOCK_TAGS_LOADING',
    SET_BREADCRUMBS: 'SET_BREADCRUMBS',
    SET_IS_SVG_LOADING: 'SET_IS_SVG_LOADING',
    SET_SHOW_ONLY_FOLDERS: 'SET_SHOW_ONLY_FOLDERS',
    SET_PSD_AS_PARSED: 'SET_PSD_AS_PARSED',
    SET_IDML_AS_PARSED: 'SET_IDML_AS_PARSED',
    UPDATE_IS_PROCESSING: 'UPDATE_IS_PROCESSING',
    SET_BYNDER_AVAILABLE: 'SET_BYNDER_AVAILABLE',
    SET_YOUTUBE_AVAILABLE: 'SET_YOUTUBE_AVAILABLE',
    SET_BYNDER_TAB_LABEL: 'SET_BYNDER_TAB_LABEL',
    SET_SALSIFY_AVAILABLE: 'SET_SALSIFY_AVAILABLE',
    SET_SALSIFY_TAB_LABEL: 'SET_SALSIFY_TAB_LABEL',
    SET_IS_PUBLISH_TO_BYNDER: 'SET_IS_PUBLISH_TO_BYNDER',
    SET_BYNDER_DOWNLOADING: 'SET_BYNDER_DOWNLOADING',
    SET_INITIAL_COMPANY_ID: 'SET_INITIAL_COMPANY_ID',
    SET_COMPANY_MISMATCH: 'SET_COMPANY_MISMATCH',
    SET_GRID_DENSITY: 'SET_GRID_DENSITY',
    SET_OPTIMIZELY_AVAILABLE: 'SET_OPTIMIZELY_AVAILABLE',
}

export const ACTIONS = {
  UPLOAD_PRODUCT_FILES: 'uploadProductFiles',
  CHECK_PRODUCT_FILES: 'checkProductFiles',
  HIDE_MODAL: 'hideModal'
}

export default {
    namespaced: true,
    state: initState(),
    getters: {
        isListLoading: state => state.isListLoading,
        items: state => state.items,
        selected: state => state.selected,
        firstSelectedItem: (state, getters) => getters.selectedItems[0],
        selectedItems: state => state.selected.map(selectedId =>
            state.items.find(item => item.id === selectedId)
            || state.selectedItemCache.find(item => item.id === selectedId)
        ).filter(Boolean),
        selectedFolders: (state, getters) => getters.selectedItems.filter(item => item.type === 'folder').map(item => item.id),
        selectedFiles: (state, getters) => getters.selectedItems.filter(item => item.type !== 'folder').map(item => item.id),
        totalCount: state => state.pagination.totalCount,
        folderCount: state => state.pagination.folderCount,
        pageSize: state => state.pagination.pageSize,
        pageSizeInvalidFeedback: state => state.pagination.pageSizeInvalidFeedback,
        pageSizeValidationState: state => state.pagination.pageSizeValidationState,
        isSelected: state => item => state.selected.includes(item.id),
        selectedCount: state => state.selected.length,
        isAnySelected: state => !!state.selected.length,
        modal: state => state.modal,
        compressSettingsData: state => state.compressSettings.data,
        isCompressSettingsFetching: state => state.compressSettings.isFetching,
        isCompressSettingsSaving: state => state.compressSettings.isSaving,
        isCompressSettingsReplaceOriginalImage: state => state.compressSettings.replaceOriginalImage,
        isDownloading: state => state.isDownloading,
        isDuplicates: state => state.isDuplicates,
        isThumbnailGenerating: state => state.isThumbnailGenerating,
        stockTags: state => state.stockTags,
        isStockTagsLoading: state => state.isStockTagsLoading,
        breadcrumbs: state => state.breadcrumbs.map(item => ({...item, text: item.name})),
        isSvgLoading: state => state.isSvgLoading,
        isOneSelected: state => state.selected.map(
          selectedId => state.items.find(item => item.id === selectedId)
        ).length === 1 ,
        showFavorites: state => state.showFavorites,
        bynderAvailable: state => state.bynderAvailable,
        youtubeAvailable: state => state.youtubeAvailable,
        salsifyAvailable: state => state.salsifyAvailable,
        isPublishToBynder: state => state.isPublishToBynder,
        isBynderDownloading: state => state.isBynderDownloading,
        hasCompanyMismatch: state => state.hasCompanyMismatch,
        gridDensity: state => state.gridDensity,
        optimizelyAvailable: state => state.optimizelyAvailable
    },
    mutations: {
        [MUTATIONS.UPDATE_IS_PROCESSING](state, payload) {
          const item = state.items.find(({ id }) => id === payload.id);
          if (!item) {
            return;
          }
          item.is_processing = payload.isProcessing;
          if (payload.percentage) {
            item.percent = payload.percentage;
          }
        },
        [MUTATIONS.SET_BYNDER_TAB_LABEL](state, label) {
          state.bynderTabLabel = label;
        },
        [MUTATIONS.SET_SALSIFY_TAB_LABEL](state, label) {
          state.salsifyTabLabel = label;
        },
        [MUTATIONS.SET_LIST_LOADING](state, payload) {
            state.isListLoading = payload;
        },
        [MUTATIONS.SET_ITEMS](state, payload) {
            state.items = payload.map(addProcessingProp)
            state.selectedItemCache = state.selected.map(selectedId =>
                state.items.find(item => item.id === selectedId)
                || state.selectedItemCache.find(item => item.id === selectedId)
            ).filter(Boolean)
        },
        [MUTATIONS.PATCH_UPLOAD_ITEMS](state, { items, append = false }) {
            items.forEach(data => {
                const item = state.items.find(item => item.id === data.id);
                if (item) Object.keys(data).forEach(key => Vue.set(item, key, data[key]));
                else if (append) state.items.push(data);
            });
        },
        [MUTATIONS.RECONCILE_ITEMS](state, items) {
            const existing = new Map(state.items.map(item => [item.id, item]));
            state.items = items.map(data => {
                const item = existing.get(data.id);
                if (!item) return addProcessingProp(data);
                Object.assign(item, data);
                return item;
            });
        },
        [MUTATIONS.FAIL_PROGRESSING](state, id) {
            const item = state.items.find(item => item.id === id);
            if (item) {
                Vue.set(item, 'upload_error', true);
                item.is_processing = 0;
            }
        },
        [MUTATIONS.SET_TOTAL_COUNT](state, payload) {
            state.pagination.totalCount = payload
        },
        [MUTATIONS.SET_FOLDER_COUNT](state, payload) {
            state.pagination.folderCount = payload
        },
        [MUTATIONS.SET_LAST_PAGE](state, payload) {
            state.pagination.lastPage = payload
        },
        [MUTATIONS.UPDATE_PROGRESS_PERCENTAGE](state, payload) {
            const item = state.items.find(({ id }) => payload.id === id);
            if (!item) {
                return;
            }
            item.percent = payload.percent;
        },
        [MUTATIONS.FINISH_PROGRESSING](state, payload) {
            const item = state.items.find(({ id }) => payload.id === id);
            if (!item) {
                return;
            }

            item.is_processing = 0;
            item.name = payload.name;
            item.original_path = payload.path;
            item.path = uploadedFileItem({ path: payload.path }).path;
            item.url = payload.url || item.path;
            item.url_shorten = payload.url_shorten || item.path;
            item.type = payload.type;
            
            if (payload.thumbnail) {
                if (item.thumbnail_path) {
                    try {
                        const url = new URL(item.thumbnail_path)
                        url.searchParams.set('file', payload.thumbnail)
                        item.thumbnail_path = url.toString()
                    } catch (e) {
                        item.thumbnail_path = `${window.location.origin}/share?file=${payload.thumbnail}`
                    }
                } else {
                    const base = item.url || item.path || `${window.location.origin}/share?file=`
                    try {
                        const url = new URL(base, window.location.origin)
                        url.searchParams.set('file', payload.thumbnail)
                        item.thumbnail_path = url.toString()
                    } catch (e) {
                        item.thumbnail_path = `${window.location.origin}/share?file=${payload.thumbnail}`
                    }
                }
            }
            
        },
        [MUTATIONS.TOGGLE_SELECTED_ITEMS](state, id) {
            if (state.selected.includes(id)) {
                state.selected.splice(state.selected.indexOf(id), 1)
            } else {
                state.selected.push(id)
            }
            state.selectedItemCache = state.selected.map(selectedId =>
                state.items.find(item => item.id === selectedId)
                || state.selectedItemCache.find(item => item.id === selectedId)
            ).filter(Boolean);
        },
        [MUTATIONS.CLEAR_SELECTION](state) {
            state.selected = [];
            state.selectedItemCache = [];
        },
        SET_SELECTION (state, payload) {
            state.selected = payload
            state.selectedItemCache = payload.map(id =>
                state.items.find(item => item.id === id)
                || state.selectedItemCache.find(item => item.id === id)
            ).filter(Boolean);
        },
        [MUTATIONS.SET_MODAL](state, payload) {
            state.modal = payload;
        },
        [MUTATIONS.COMPRESS_SETTINGS.SET_FETCHING](state, payload) {
            state.compressSettings.isFetching = payload;
        },
        [MUTATIONS.COMPRESS_SETTINGS.SET_SAVING](state, payload) {
            state.compressSettings.isSaving = payload;
        },
        [MUTATIONS.COMPRESS_SETTINGS.SET_REPLACE_ORIGINAL_IMAGE](state, payload) {
            state.compressSettings.replaceOriginalImage = payload;
        },
        [MUTATIONS.COMPRESS_SETTINGS.SET_DATA](state, payload) {
            state.compressSettings.data = payload;
        },
        [MUTATIONS.COMPRESS_SETTINGS.SET_DATA_KEY](state, { key, value }) {
            state.compressSettings.data[key] = value;
        },
        [MUTATIONS.SET_DOWNLOADING](state, payload) {
            state.isDownloading = payload;
        },
        [MUTATIONS.SET_IS_DUPLICATES](state, payload) {
          state.isDuplicates = payload;
        },
        [MUTATIONS.SET_THUMBNAIL_GENERATING](state, payload) {
            state.isThumbnailGenerating = payload;
        },
        [MUTATIONS.UPDATE_THUMBNAIL_BY_ID](state, { id, thumbnail }) {
            const itemIndex = state.items.findIndex(item => item.id === id);
            if (itemIndex < 0) {
                return;
            }
            state.items.splice(itemIndex, 1, { ...state.items[itemIndex], url: thumbnail })
        },
        [MUTATIONS.UPDATE_ITEM_NAME](state, { id, name }) {
            const item = state.items.find(item => item.id === id);

            if (!item) {
                return;
            }

            item.name = name;
        },
        [MUTATIONS.UPDATE_ITEM_CUSTOMIZATION](state, { id, color, emoji }) {
            const itemIndex = state.items.findIndex(item => item.id === id);

            if (itemIndex < 0) {
                return;
            }

            state.items.splice(itemIndex, 1, {
                ...state.items[itemIndex],
                color,
                emoji,
            });
        },
        UPDATE_ITEMS (state, updatedItems) {
            updatedItems.forEach(updatedItem => {
                const index = state.items.findIndex(item => item.id === updatedItem.id)
                if (index < 0) {
                    return
                }
                state.items.splice(index, 1, updatedItem)
            })
        },
        [MUTATIONS.SET_STOCK_TAGS](state, payload) {
            state.stockTags = payload;
        },
        [MUTATIONS.SET_STOCK_TAGS_LOADING](state, is) {
            state.isStockTagsLoading = is;
        },
        [MUTATIONS.SET_BREADCRUMBS](state, payload) {
            state.breadcrumbs = payload;
        },
        [MUTATIONS.SET_IS_SVG_LOADING](state, payload) {
            state.isSvgLoading = payload;
        },
        SET_SHOW_FAVORITES (state, payload) {
            state.showFavorites = payload
        },
        [MUTATIONS.SET_SHOW_ONLY_FOLDERS] (state, payload) {
            state.showOnlyFolders = payload
        },
        [MUTATIONS.SET_PSD_AS_PARSED](state, payload) {
            const index = state.items.findIndex(item => item.id === payload.fileId);
            
            if (index) {
              state.items[index].is_psd_parsed = true;
            }
        },
        [MUTATIONS.SET_IDML_AS_PARSED](state, payload) {
            const index = state.items.findIndex(item => item.id === payload.fileId);

            if (index === -1) {
                return;
            }

            state.items.splice(index, 1, {
                ...state.items[index],
                is_idml_parsed: true,
                thumbnail_path: payload.thumbnailPath || state.items[index].thumbnail_path,
            });
        },
        SET_PAGE_SIZE (state, payload) {
            state.pagination.pageSize = payload
        },
        SET_PAGE_SIZE_VALIDATION (state, payload) {
            state.pagination.pageSizeInvalidFeedback = payload.pageSizeInvalidFeedback
            state.pagination.pageSizeValidationState = payload.pageSizeValidationState
        },
        [MUTATIONS.SET_BYNDER_AVAILABLE](state, payload) {
            state.bynderAvailable = payload;
        },
        [MUTATIONS.SET_YOUTUBE_AVAILABLE](state, payload) {
            state.youtubeAvailable = payload;
        },
        [MUTATIONS.SET_SALSIFY_AVAILABLE](state, payload) {
            state.salsifyAvailable = payload;
        },
        [MUTATIONS.SET_IS_PUBLISH_TO_BYNDER](state, payload) {
            state.isPublishToBynder = payload;
        },
        [MUTATIONS.SET_BYNDER_DOWNLOADING](state, payload) {
            state.isBynderDownloading = payload;
        },
        [MUTATIONS.SET_INITIAL_COMPANY_ID](state, payload) {
            state.initialCompanyId = payload;
        },
        [MUTATIONS.SET_COMPANY_MISMATCH](state, payload) {
            state.hasCompanyMismatch = payload;
        },
        [MUTATIONS.SET_GRID_DENSITY](state, payload) {
            state.gridDensity = clampGridDensity(payload);
        },
        [MUTATIONS.SET_OPTIMIZELY_AVAILABLE](state, payload) {
            state.optimizelyAvailable = payload;
        },
    },
    actions: {
        updatePreparedUploadPagination({ state, commit }, { uploads, perPage }) {
            // The server marks replacements/existing files as isExist; only new records grow the list.
            const newFileCount = uploads.reduce((count, upload) => count + (upload.isExist ? 0 : 1), 0);
            const totalCount = state.pagination.totalCount + newFileCount;
            const lastPage = Math.max(1, Math.ceil(totalCount / perPage));
            commit(MUTATIONS.SET_TOTAL_COUNT, totalCount);
            commit(MUTATIONS.SET_LAST_PAGE, lastPage);
            return { totalCount, lastPage, newFileCount };
        },
        patchPreparedUploads({ commit }, { uploads, append = false }) {
            commit(MUTATIONS.PATCH_UPLOAD_ITEMS, {
                items: uploads.filter(upload => upload.user_file).map(upload => ({
                    ...uploadedFileItem(upload.user_file), is_processing: 1,
                })),
                append,
            });
        },
        async fetchList({ commit, state, dispatch }, dto = fetchListRouterBasedActionCreator(router.currentRoute)) {
            if (state.hasCompanyMismatch) {
                if (!dto.background) commit(MUTATIONS.SET_LIST_LOADING, false);
                return;
            }

            const selectedCompanyId = $('#company_selector').val();
            const currentCompanyId = Array.isArray(selectedCompanyId)
                ? (selectedCompanyId[0] == null ? '' : String(selectedCompanyId[0]))
                : (selectedCompanyId == null ? '' : String(selectedCompanyId));
            let storedCompanyId = localStorage.getItem('selected-company-id');

            if (state.initialCompanyId === null) {
                if (currentCompanyId && storedCompanyId !== currentCompanyId) {
                    localStorage.setItem('selected-company-id', currentCompanyId);
                    storedCompanyId = currentCompanyId;
                }

                const initialId = currentCompanyId || storedCompanyId;
                commit(MUTATIONS.SET_INITIAL_COMPANY_ID, initialId);

                if (!storedCompanyId && currentCompanyId) {
                    localStorage.setItem('selected-company-id', currentCompanyId);
                }
            }

            if (storedCompanyId && state.initialCompanyId && storedCompanyId !== state.initialCompanyId) {
                commit(MUTATIONS.SET_COMPANY_MISMATCH, true);

                toastr.options = {
                    timeOut: 0,
                    extendedTimeOut: 0,
                    closeButton: true,
                    progressBar: false,
                };

                toastr.warning(
                    '<div style="color: #000;">You are viewing data for a different company. Please reload the page to see current company data.</div>',
                    '<div style="color: #000; font-weight: bold;">Company Context Changed</div>'
                );

                if (!dto.background) commit(MUTATIONS.SET_LIST_LOADING, false);
                return;
            }

            if (!dto.background) commit(MUTATIONS.SET_LIST_LOADING, true)
            const source = resolveSource(dto)
            const folderId = router.currentRoute.params.pathMatch?.split('/')?.pop() || 0;
            let productFolderId = dto.params.pathMatch?.split('/')?.pop() || null;

            const tab = dto.params.tab;

            try {
                const { data } = await FileBrowser.listBySource(source, {
                    pageSize: dto.query?.pageSize || state.pagination.pageSize,
                    folder_id: tab === TABS.PRODUCTS ? productFolderId : API_TABS.includes(tab) ? null : folderId,
                    favorites: (dto.query?.favorites ?? state.showFavorites) || null,
                    showOnlyFolders: state.showOnlyFolders || null,
                    includeId: router.currentRoute.query.includeId || null
                })

                if (API_TABS.includes(tab) && data?.status === 'error') {
                    toastr.options = {
                        timeOut: 0,
                        extendedTimeOut: 0
                    };
                    toastr.error(data?.message);
                    console.error(data?.message);

                    return;
                }

                if (dto.canApply && !dto.canApply()) return;

                const dataMapper = listMapperFactory(dto);
                commit(dto.background ? MUTATIONS.RECONCILE_ITEMS : MUTATIONS.SET_ITEMS, dataMapper(data.items))
                commit(MUTATIONS.SET_TOTAL_COUNT, data.totalCount)
                commit(MUTATIONS.SET_FOLDER_COUNT, data.folderCount || 0)
                commit(MUTATIONS.SET_LAST_PAGE, data.last_page)
                if (!dto.preserveSelection) {
                    commit(MUTATIONS.CLEAR_SELECTION)
                }
                if (!dto.background) commit(MUTATIONS.SET_LIST_LOADING, false)

                const queryParamsToRemove = ['includeId', 'fileId'];
                queryParamsToRemove.forEach(param => {
                  if (!router.currentRoute.query[param]) {
                    return;
                  }

                  delete router.currentRoute.query[param];
                });

                if (data.current_file && !Array.isArray(data.current_file) && data.current_file.parents)  {
                  const newRoute = { query: { ...router.currentRoute.query, silent: true }, params: { ...router.currentRoute.params } };
                  newRoute.query.page = data.current_page;
                  newRoute.params.pathMatch = data.current_file.parents.length > 1
                    ? data.current_file.parents.join('/')
                    : String(data.current_file.parentId);
                  commit(MUTATIONS.TOGGLE_SELECTED_ITEMS, data.current_file.id);
                  await router.push(newRoute);
                  await dispatch('fetchBreadcrumbs');
                }
            } catch (error) {
                if (error.code === 'ERR_CANCELED') {
                    return;
                }
                if (!dto.background) commit(MUTATIONS.SET_LIST_LOADING, false)
                throw error;
            }
        },
        async navigateToUploadedFile({ state, dispatch }) {
            const currentPage = Number(router.currentRoute.query.page);
  
            if (state.pagination.totalCount % state.pagination.pageSize === 0 && state.pagination.lastPage !== 0) {
                await router.push({ query: { ...router.currentRoute.query, page: state.pagination.lastPage + 1 } });
              
            } else if (currentPage !== state.pagination.lastPage && state.pagination.lastPage !== 0) {
                await router.push({ query: { ...router.currentRoute.query, page: state.pagination.lastPage } });
              
            } else {
                await dispatch('fetchList', fetchListDataBasedActionCreator(
                    router.currentRoute.params.tab,
                    router.currentRoute.params.pathMatch,
                    router.currentRoute.query.page,
                    router.currentRoute.query.search,
                    router.currentRoute.query.type,
                ));
            }
        },
        async addFolder({ dispatch, state }, parent_id) {
            let res = await FileBrowser.addFolder(parent_id, '/file/create_folder?tab='+router.currentRoute.params.tab)

            if (router.currentRoute.query.search) {
              router.currentRoute.query.includeId = res.data.id
            }
            
            const currentPage = Number(router.currentRoute.query.page) || 1

            // for products all the folders are shown on page 1, so we want to stay there.
            let targetPage = 1

            if (router.currentRoute.params.tab !== TABS.PRODUCTS) {
                // inside a folder every sub-folder is listed on every page, so the new one is already here
                targetPage = router.currentRoute.params.pathMatch
                    ? currentPage
                    : Math.ceil((state.pagination.totalCount + 1) / state.pagination.pageSize)
            }

            if (targetPage !== currentPage) {
                router.push({ query: { ...router.currentRoute.query, page: targetPage } })
            } else {
                dispatch('fetchList')
            }
        },
        async fetchCompressSettings({ commit }) {
            commit(MUTATIONS.COMPRESS_SETTINGS.SET_FETCHING, true)
            const [{ data: compressSettings }, { data: gridDensity }] = await Promise.all([
                FileBrowser.getSettingsValue(),
                FileBrowser.gridDensity(),
            ])
            commit(MUTATIONS.COMPRESS_SETTINGS.SET_DATA, compressSettings)
            commit(MUTATIONS.SET_GRID_DENSITY, clampGridDensity(gridDensity))
            commit(MUTATIONS.COMPRESS_SETTINGS.SET_FETCHING, false)
            commit(MUTATIONS.COMPRESS_SETTINGS.SET_REPLACE_ORIGINAL_IMAGE, false)
        },
        async saveCompressSettings({ state, commit }) {
            commit(MUTATIONS.COMPRESS_SETTINGS.SET_SAVING, true)
            await Promise.all([
                FileBrowser.saveSettingsValue(state.compressSettings.data),
                FileBrowser.gridDensity(state.gridDensity),
            ])
            commit(MUTATIONS.COMPRESS_SETTINGS.SET_SAVING, false)
        },
        async fetchGridDensity({ commit }) {
            const { data } = await FileBrowser.gridDensity()
            commit(MUTATIONS.SET_GRID_DENSITY, clampGridDensity(data))
        },
        // `router.currentRoute.params.tab` is only set on the standalone `/file/browser/:tab`
        // page; off-route embeddings (e.g. Run Reports' "Import From" picker) fall back to
        // the fileUploadImages store tab instead (same pattern as ItemActionsDropdown.vue's
        // `currentTab`). The trailing `dispatch('fetchList')` is skipped off-route for the
        // same reason: this action has no way to know which folder/page the embedding modal
        // is showing, so SelectFolderModalContentView.vue refreshes itself instead (see its
        // `modal` watcher).
        async deleteSelectedItem({ state, getters, dispatch, rootGetters }) {
            const deletedPsdPaths = getters.selectedItems
                .filter(item => [FILE_TYPE.PSD, FILE_TYPE.PSB].includes(item.type))
                .map(item => item.original_path || item.path);
            const routeTab = router.currentRoute.params.tab;
            const tab = routeTab || rootGetters['fileUploadImages/getTab'];
            const method = resolveMultipleDeleteMethod(tab)

            if (tab == TABS.PRODUCTS) {
                await method(getters.selectedFiles,getters.selectedFolders)
            }else if(tab ==  TABS.STOCK_IMAGES) {
                await method(getters.selectedItems,getters.selectedFolders)
            }else if(tab ==  TABS.BACKGROUND) {
                await method(getters.selectedItems,getters.selectedFolders)
            }else{
                await method(state.selected)
            }
            deletedPsdPaths.forEach(clearPsdImageSegmentationJobs);
            if (routeTab) {
                dispatch('fetchList');
            }
        },
        // See deleteSelectedItem above for the tab-fallback and fetchList rationale.
        async deleteById({ commit, dispatch, rootGetters }, item) {
            try {
                const routeTab = router.currentRoute.params.tab;
                const tab = routeTab || rootGetters['fileUploadImages/getTab'];
                const method = resolveDeleteItemMethod(tab, item)

                let path = item.path;

                if (path && path.includes('file=')) {
                    path = path.split('file=').pop();
                }
                if (tab == TABS.PRODUCTS) {
                    await method(item.id);
                } else if (item.type === FILE_TYPE.FOLDER && item.confirmBriefUnlink) {
                    await Requests.deleteFolder(item.id, { confirmBriefUnlink: true });
                } else {
                    await method(item.id, path);
                }

                if ([FILE_TYPE.PSD, FILE_TYPE.PSB].includes(item.type)) {
                    clearPsdImageSegmentationJobs(item.original_path || path);
                }

                if (routeTab) {
                    dispatch('fetchList');
                }
            } finally {
                commit(MUTATIONS.SET_MODAL, createModal())
            }
        },
        // See deleteSelectedItem above for the tab-fallback rationale.
        async editFile({ commit, rootGetters }, { id, name, parent_id, item, showSuccess = true, brief_rename_action = null }) {
            let openBriefRenameModal = false;

            try {
                const isRename = typeof name !== 'undefined';
                const itemType = getEditableItemType(item);
                const tab = router.currentRoute.params.tab || rootGetters['fileUploadImages/getTab'];

                if (tab == TABS.PRODUCTS) {
                  if (itemType === FILE_TYPE.FOLDER) {
                    await Requests.updateFileFolderFile({ id, name, parent_id });
                  } else {
                    await Requests.updateFile(id, { name });
                  }
                } else {
                    await Requests.updateFolderFile({
                        id,
                        name,
                        parent_id,
                        brief_rename_action,
                    })
                }

                if (isRename) {
                    commit(MUTATIONS.UPDATE_ITEM_NAME, { id, name })
                }

                if (showSuccess) {
                    if (isRename) {
                        toastr.success(`${_.upperFirst(itemType)} renamed successfully`)
                    } else {
                        toastr.success('Item moved successfully')
                    }
                }

                return true;
            } catch (error) {
                if (
                    error.response?.status === 409
                    && error.response?.data?.brief_rename_confirmation_required
                ) {
                    openBriefRenameModal = true;
                    commit(MUTATIONS.SET_MODAL, createModal(MODALS.BRIEF_FOLDER_RENAME, {
                        id,
                        name,
                        brief_title: error.response.data.brief_title,
                        brief_id: error.response.data.brief_id,
                        item,
                    }));
                    return false;
                }

                if (!error.response?.data?.message && !error.response?.data?.errors) {
                    toastr.error(`Something went wrong on ${typeof name !== 'undefined' ? 'updating name' : 'moving item'}`)
                }

                return false;
            } finally {
                if (!openBriefRenameModal) {
                    commit(MUTATIONS.SET_MODAL, createModal());
                }
            }
        },

        async customizeFolder({ commit }, { id, color, emoji }) {
            try {
                const payload = { id, color, emoji };

                if (router.currentRoute.params.tab == TABS.PRODUCTS) {
                    await Requests.updateFileFolderFile(payload);
                } else {
                    await Requests.updateFolderFile(payload);
                }

                commit(MUTATIONS.UPDATE_ITEM_CUSTOMIZATION, { id, color, emoji });
                toastr.success('Folder customized successfully');
                return true;
            } catch (error) {
                if (!error.response?.data?.message && !error.response?.data?.errors) {
                    toastr.error('Something went wrong on customizing folder');
                }
                return false;
            } finally {
                commit(MUTATIONS.SET_MODAL, createModal());
            }
        },

        async dropProductFile({ commit }, { id, parent_id, type }) {
            try {
                await Requests.dropFileFolder({ id, parent_id, type });
                return true;
            } catch (error) {
                console.log (error);
                if (!error.response?.data?.message && !error.response?.data?.errors) {
                    toastr.error('Something went wrong on updating file')
                }

                return false;
            } 
        },        
        async download({ commit, state, getters }, payload) {
            commit(MUTATIONS.SET_DOWNLOADING, true);

            try {
              const route = router.currentRoute
              const tab = route.params.tab

              const items = getters.selectedItems

              if (tab === TABS.BOX_ASSETS) {
                if (items.length === 1) {
                  const { data } = await FileBrowser.importBoxAssets([{
                    id: items[0].id,
                    name: items[0].name,
                    extension: items[0].extension,
                    download_url: items[0].download_url,
                  }])

                  const url = data?.files?.[0]?.url

                  if (_.isEmpty(url)) {
                    throw new Error('Downloading error')
                  }

                  downloadFileLink(url, items[0].name)
                } else {
                  const { data } = await FileBrowser.downloadBoxAssets(state.selected)

                  if (_.isEmpty(data.url)) {
                    throw new Error('Downloading error')
                  }

                  downloadFileLink(data.url, data.filename || data.fileName)
                }

                return
              }

              const method = resolveDownloadMethod(route, items)

              const isBackgroundOrStock =
                [TABS.BACKGROUND, TABS.STOCK_IMAGES].includes(tab)

              const requestPayload = {
                tab,                                  
                items: isBackgroundOrStock ? items : null,
                file_ids: isBackgroundOrStock ? null : state.selected,
              }

              let response;

              if ([TABS.BYNDER_ASSETS, TABS.SALSIFY_ASSETS, TABS.FILES].includes(tab)) {
                response = await method(state.selected);
              } else {
                response = await method(requestPayload);
              }

              const data = response.data;

                if (_.isEmpty(data.url)) {
                    throw new Error('Downloading error')
                }

                downloadFileLink(data.url, data.filename || data.fileName)
            } catch (error) {
                console.log('Download error: ', error);

                if (error.response?.status === 404) {
                    toastr.error(error.response.data.message);
                } else {
                    toastr.error("Download files failed. Try again later.");
                }
            } finally {
                commit(MUTATIONS.SET_DOWNLOADING, false)
            }
        },
        async updateThumbnails({ commit, state }){
            commit(MUTATIONS.SET_THUMBNAIL_GENERATING, true);
            try {
                const { data } = await FileBrowser.updateThumbnails(state.selected)
                if (data.thumbnails) {
                    for (const id in data.thumbnails) {
                        commit(MUTATIONS.UPDATE_THUMBNAIL_BY_ID, { id: Number(id), thumbnail: `${data.thumbnails[id]}&${new Date().getTime()}` })
                    }
                }
            } catch (error) {
                toastr.error('Thumbnail generation failed. Try again later.')
            } finally {
                commit(MUTATIONS.SET_THUMBNAIL_GENERATING, false);
            }
        },
        async parsePSD({ commit, dispatch }, state) {
            commit(MUTATIONS.SET_LIST_LOADING, true)
            let success = false;

            try {
                await Requests.addPSDAssetFolder(state)
                success = true;
                toastr.success(`Upload successfully completed`);
                dispatch('fetchList');
            } catch (error) {
                toastr.error(`Upload failed`);
            } finally {
                psdLayersEventBroker.fire(PSD_LAYERS_EVENT.SAVE_PSD_ASSETS_TO_FOLDER_END, success);
                commit(MUTATIONS.SET_LIST_LOADING, false)
            }
        },
      
        async uploadScreenRecording({ commit, getters, state, dispatch }, { file }) {
            const { data: folder } = await FileBrowser.getFolderByName('Screen recording');

            let folderId = folder[0]?.id;

            if (!folder.length && !folderId) {
              let url = '/file/create_folder_by_name?tab=files';

              const { data: { id } } = await Requests.createFolderByName(url, {
                name: 'Screen recording',
              })

              folderId = id;
            }
            
            Requests.sendSharedAction({
                id: 25730,
                files: 1,
                action: 'upload',
            });

            const data = (await uploadFolderFile([file], folderId)).map(item => ({
              ...item,
              file
            }));
            
            const exists = data.filter(({ isExist, uploadId }) => isExist && !uploadId);
            
            if (exists.length) {
              const data = (await uploadFolderFile([file], folderId, 3)).map(item => ({
                ...item,
                file
              }));

              const toUpload = data.filter(({ isExist, uploadId }) => !isExist || uploadId);
              const batch = createUploadBatch(toUpload.length);

              const results = await Promise.all(toUpload.map(item => dispatch('uploadFile', { ...item, batch })));

              if (results.some(succeeded => !succeeded)) {
                  throw new Error('Screen recording upload failed');
              }

              return { folderId, fileId: data[0].id };
            }

            const toUpload = data.filter(({ isExist, uploadId }) => !isExist || uploadId);
            const batch = createUploadBatch(toUpload.length);

            const results = await Promise.all(toUpload.map(item => dispatch('uploadFile', { ...item, batch })))

            if (results.some(succeeded => !succeeded)) {
                throw new Error('Screen recording upload failed');
            }

            return { folderId, fileId: data[0].id };
        },

        async uploadFiles({ commit, getters, state, dispatch }, {
            files,
            replace,
            folderId,
            background = shouldUseBackgroundUploads(files),
        }) {
            const resolvedFolderId = folderId || router.currentRoute.params.pathMatch?.split('/').pop();
            let uploadRoute = router.currentRoute;

            const sourceFolderId = uploadRoute.params.pathMatch?.split('/').pop();
            const shouldShowUploads = (Number(resolvedFolderId) || 0) === (Number(sourceFolderId) || 0);

            if (window.backgroundUploadContext?.userId && background) {
                try {
                    return await enqueueBackgroundUploads({ files, folderId: resolvedFolderId, replace });
                } catch (error) {
                    toastr.warning(error.message);
                    throw error;
                }
            }

            const data = (await uploadFolderFile(files, resolvedFolderId, replace)).map(item => ({
                ...item,
                file: files[Number(item.data.key)],
            }));

            const isRootLevelUpload = data.length > 0 && data[0].isRootLevel && data[0].folderId;

            if (isRootLevelUpload && shouldShowUploads && router.currentRoute === uploadRoute) {
                const createdFolderId = data[0].folderId;

                await router.push({
                    path: `${router.currentRoute.path}/${createdFolderId}`,
                    query: { ...router.currentRoute.query, page: 1 }
                });
                uploadRoute = router.currentRoute;
            }

            if (shouldShowUploads && router.currentRoute === uploadRoute) {
                const canApply = () => router.currentRoute === uploadRoute;

                await dispatch('fetchList', {
                    params: uploadRoute.params,
                    query: uploadRoute.query,
                    preserveSelection: true,
                    canApply,
                });

                const lastPage = state.pagination.lastPage;

                if (canApply() && lastPage > 0 && Number(uploadRoute.query.page) !== lastPage) {
                    await router.push({ query: { ...uploadRoute.query, page: lastPage } });
                }
            }

            queueUploads({ commit, dispatch }, {
                data,
                folderId: resolvedFolderId,
                fileCount: files.length,
            });
        },

        // Router-free upload: folderId is explicit. Await onPrepared before starting transfers so
        // the target page and progress tiles exist. Return the queued count for existing callers.
        async uploadFilesToFolder({ commit, dispatch }, { files, folderId, replace = 0, onPrepared = null, onSettled = null }) {
            const data = (await uploadFolderFile(files, folderId, replace, Boolean(onPrepared))).map(item => ({
                ...item,
                file: files[Number(item.data.key)],
            }));

            if (onPrepared) {
                try {
                    await onPrepared(data.filter(({ isExist, uploadId }) => !isExist || uploadId));
                } catch (error) {
                    // A failed list request must not prevent the prepared files from uploading.
                    console.error('Could not display prepared uploads', error);
                    toastr.warning('Files are uploading, but the list could not be updated.');
                }
            }

            return queueUploads({ commit, dispatch }, {
                data,
                folderId,
                fileCount: files.length,
                onPrepared,
                onSettled,
            });
        },

        async uploadFile({ commit, dispatch }, payload) {
            const { batch, file, ...data } = payload;
            startUpload();
            const size = payload.data.size;
            const chunks = Math.ceil(size / CHUNK_SIZE);
            let succeeded = false;

            try {
                let chunk = 1;
                while (chunk <= chunks) {
                    const offset = (chunk - 1) * CHUNK_SIZE;
                    const signed_res = await Requests.signPartUpload({ ...data, partNumber: chunk });
                    const chunk_file = file.slice(offset, offset + CHUNK_SIZE);
                    await uploadFile(signed_res.data.url, chunk_file, percent => {
                        commit(MUTATIONS.UPDATE_PROGRESS_PERCENTAGE, {
                            percent: 100 / chunks * chunk - (1 - percent) * 100 / chunks,
                            id: payload.id
                        });
                    });
                    chunk++;
                }

                const res = await Requests.getMultipartUploadedParts(data);
                const response = await Requests.completeMultipartUpload({
                    ...data,
                    parts: res.data.parts,
                });

                const { user_file } = response.data;

                commit(MUTATIONS.FINISH_PROGRESSING, {
                  id: payload.id,
                  thumbnail: response.data.thumbnail,
                  name: user_file.name,
                  path: user_file.path,
                  url: user_file.url,
                  url_shorten: user_file.url_shorten,
                  type: user_file.type,
                });

                succeeded = true;

                if (response.data.user_file.psd_data) {
                    commit(MUTATIONS.SET_PSD_AS_PARSED, { fileId: response.data.user_file.id });
                }

                if (response.data.isAutoPSDParsingProcess) {
                    toastr.info(`Parsing PSD is in progress, please check your email for the current status`);
                }

                response.data.warnings.forEach(warning => {
                  toastr.warning(warning);
                });
            } catch (e) {
                if (!succeeded) {
                    commit(MUTATIONS.FAIL_PROGRESSING, payload.id);
                    if (e.response?.data?.message) {
                        toastr.error(e.response.data.message);
                    } else {
                        toastr.error(`Uups. Something went wrong!`);
                    }
                } else {
                    console.error(e);
                }
            } finally {
                settleBatchItem(batch, succeeded);
                finishUpload(dispatch, batch);
            }

            return succeeded;
        },

        async storeExternalLinks({ commit, getters, state, dispatch }, { files, replace, folderId }) {
            const resolvedFolderId = folderId || router.currentRoute.params.pathMatch?.split('/').pop();
            if (!resolvedFolderId) return;
             
            const data = (await uploadFolderLink(files, resolvedFolderId, replace)).map(item => ({
                ...item,
                file: files[Number(item.data.key)],
            }));

            await dispatch('navigateToUploadedFile');

            toastr.success(`Upload successfully completed`);
        },

        async compress({ commit, getters, dispatch }, {settings, replaceOriginalImage}) {
            if (getters.selectedItems.length === 1) {
                try {
                    toastr.info("Compressing file...", '', {
                      timeOut: 0, extendedTimeOut: 0
                    });
                    await Requests.compressFolderFile(getters.selectedItems[0].id, settings, replaceOriginalImage)
                    dispatch('fetchList')
                    toastr.clear()
                    toastr.success(
                      "Done!"
                    );
                } catch (error) {
                    toastr.error('Something went wrong while compressing.')
                } finally {
                    commit(MUTATIONS.CLEAR_SELECTION);
                }
            } else {
                try {
                    toastr.info("Compressing files...", '', {
                      timeOut: 0, extendedTimeOut: 0
                    });
                    await Requests.compressFolderFileUsingBackgroundJob(
                        getters.selectedItems.map(({ id }) => id),
                        settings,
                        replaceOriginalImage
                    )
                    dispatch('fetchList')
                    toastr.clear()
                    toastr.success(
                        "Done!"
                    );
                } catch (error) {
                    toastr.error('Something went wrong while compressing.')
                } finally {
                    commit(MUTATIONS.CLEAR_SELECTION);
                }
            }
        },
        async fetchStockTags({ commit }) {
            try {
                commit(MUTATIONS.SET_STOCK_TAGS_LOADING, true)
                const { data } = await Requests.fetchStockTags();
                commit(MUTATIONS.SET_STOCK_TAGS, data);
            } catch (error) {
                toastr.error('Something went wrong while fetching tags.')
            } finally {
                commit(MUTATIONS.SET_STOCK_TAGS_LOADING, false)
            }
        },
        async fetchBreadcrumbs({ commit }){
            if (!router.currentRoute.params.pathMatch) {
                return commit(MUTATIONS.SET_BREADCRUMBS, [])
            }
            try {
                const { data } = await Requests.getBreadcrumbs(router.currentRoute.params.pathMatch.split('/'), router.currentRoute.params.tab);
                commit(MUTATIONS.SET_BREADCRUMBS, data)
            } catch (error) {
                console.log(error)
            }
        },
        clearSelection ({ commit }) {
            commit(MUTATIONS.CLEAR_SELECTION)
        },
        selectAll ({ commit, state }) {
            commit('SET_SELECTION', _.uniq([...state.selected, ..._.map(_.reject(state.items, (item, key) => item.id === null || (key === 0 && item.name === "...")), 'id')]));
        },
        setIsSvgLoading({ commit }, payload) {
            commit(MUTATIONS.SET_IS_SVG_LOADING, payload);
        },
        
        async duplicateFiles({ commit, dispatch, state }) {
            try {
                commit(MUTATIONS.SET_LIST_LOADING, true);
                
                const { data } = await Requests.duplicateFiles(state.selected);
                
                dispatch('fetchList');
                
                toastr.success(`${data.length > 1 ? 'Files were' : 'File was'} copied successfully!`);
            } catch (error) {
                toastr.error('Something went wrong while duplicating files.');
            } finally {
                commit(MUTATIONS.SET_LIST_LOADING, false);
            }
        },

        async copyFolders({ commit, dispatch, state }) {
            try {
                commit(MUTATIONS.SET_LIST_LOADING, true);

                const { data } = await Requests.copyFolders(state.selected);

                await dispatch('fetchList');

                const successCount = data.success?.length || 0;
                const failedCount = data.failed?.length || 0;

                if (successCount > 0 && failedCount === 0) {
                    toastr.success(
                        `${successCount > 1 ? `${successCount} folders were` : 'Folder was'} copied successfully!`
                    );
                    return;
                }

                if (successCount > 0 && failedCount > 0) {
                    const firstFailedReason = data.failed?.[0]?.reason;

                    toastr.warning(
                        `${successCount} ${successCount > 1 ? 'folders were' : 'folder was'} copied, ` +
                        `but ${failedCount} ${failedCount > 1 ? 'folders failed' : 'folder failed'}` +
                        `${firstFailedReason ? `. First error: ${firstFailedReason}` : '.'}`
                    );
                    return;
                }

                if (failedCount > 0 && successCount === 0) {
                    const firstFailedReason = data.failed?.[0]?.reason;

                    toastr.error(
                        failedCount > 1
                            ? `${failedCount} folders failed. First error: ${firstFailedReason || 'Unknown error.'}`
                            : (firstFailedReason || 'Folder was not copied.')
                    );
                    return;
                }

                toastr.error('No folders were copied.');
            } catch (error) {
                toastr.error('Something went wrong while copying folders.');
            } finally {
                commit(MUTATIONS.SET_LIST_LOADING, false);
            }
        },
      
        async uploadProductFiles({ state, dispatch, commit }, payload) {
            try {
                commit(MUTATIONS.SET_LIST_LOADING, true);
                
                const formData = prepareProductFileForm(payload);
    
                const { data } = await FileBrowser.storeProductFiles(formData);

                // await dispatch('navigateToUploadedFile'); TODO: try revert
                if (!payload.skipNavigation) {
                    await dispatch('navigateToUploadedFile');
                } else {
                    commit(MUTATIONS.SET_LIST_LOADING, false);
                }
                
                toastr.success(`${data.filenames.length > 1 ? 'Files were' : 'File was'} uploaded successfully!`);
            } catch (error) {
                commit(MUTATIONS.SET_LIST_LOADING, false);
                toastr.error('Something went wrong while uploading files.');
            }
        },

       async hideModal({ state, dispatch, commit }, payload) {
            commit(MUTATIONS.SET_LIST_LOADING, false);
            await dispatch('navigateToUploadedFile');
       },

        async checkProductFiles({ state, dispatch, commit }, payload) {
            try {
                commit(MUTATIONS.SET_LIST_LOADING, true);
      
                const formData = prepareProductFileForm(payload);

                const { data } = await FileBrowser.checkProductFilesExist(formData);
                
                commit(MUTATIONS.SET_IS_DUPLICATES, data.isDuplicates);
            } catch (error) {
                commit(MUTATIONS.SET_LIST_LOADING, false);
                toastr.error('Something went wrong while uploading files.');
            }
        },

        async toggleFavorites ({ commit, state, dispatch }, { action, ids, verbose }) {
            try {
                const { data: { message } } = await FileBrowser[action](ids, verbose)
                toastr.success(message)
                const updatedItems = state.items
                  .filter(item => ids.includes(item.id))
                  .map(item => ({
                      ...item,
                      is_favorite: !item.is_favorite,
                  }))
                commit('UPDATE_ITEMS', updatedItems)

                if (state.showFavorites) {
                    state.items = _.filter(state.items, ['is_favorite', true])
                    commit('SET_ITEMS', state.items)
                }
            } catch (error) {
                toastr.error(error.message)
            } finally {
                dispatch('clearSelection')
            }
        },

        setShowOnlyFolders({ commit }, payload) {
            commit(MUTATIONS.SET_SHOW_ONLY_FOLDERS, payload)
        },

        async fetchPageSize ({ commit }) {
            const { data } = await FileBrowser.itemsPerPage()
            commit('SET_PAGE_SIZE', data)
        },

        async updatePageSize ({ commit, dispatch, state }, payload) {
            let pageSizeInvalidFeedback = null
            let pageSizeValidationState = null
            try {
                const { data } = await FileBrowser.itemsPerPage(payload)
                commit('SET_PAGE_SIZE', data)

                const currentPage = Number(router.currentRoute.query.page) || 1
                const nextPage = clampPage(
                    currentPage,
                    state.pagination.totalCount,
                    data,
                )

                if (nextPage !== currentPage) {
                    try {
                        await router.push({ query: { ...router.currentRoute.query, page: nextPage } })
                    } catch (error) {
                        dispatch('fetchList')
                    }
                } else {
                    dispatch('fetchList')
                }
            } catch (error) {
                pageSizeInvalidFeedback = Object.values(error.response.data.errors).flat().at(0)
                pageSizeValidationState = false
            } finally {
                commit('SET_PAGE_SIZE_VALIDATION', { pageSizeInvalidFeedback, pageSizeValidationState })
            }
        },

        async initializePlatformAvailability({ commit }) {
            try {
                const bynderResponse = await FileBrowser.isPlatformAvailable('bynder');
                commit(MUTATIONS.SET_BYNDER_AVAILABLE, bynderResponse.data.available);
                commit(MUTATIONS.SET_BYNDER_TAB_LABEL, bynderResponse.data.label);

                const salsifyResponse = await FileBrowser.isPlatformAvailable('salsify');
                commit(MUTATIONS.SET_SALSIFY_AVAILABLE, salsifyResponse.data.available);
                commit(MUTATIONS.SET_SALSIFY_TAB_LABEL, salsifyResponse.data.label);

                const optimizelyResponse = await FileBrowser.isPlatformAvailable('optimizely');
                commit(MUTATIONS.SET_OPTIMIZELY_AVAILABLE, optimizelyResponse.data.available);

                const youtubeResponse = await FileBrowser.isPlatformAvailable('youtube');
                commit(MUTATIONS.SET_YOUTUBE_AVAILABLE, youtubeResponse.data.available);
            } catch (error) {
                console.error('Error checking platform availability:', error);
                commit(MUTATIONS.SET_BYNDER_AVAILABLE, false);
                commit(MUTATIONS.SET_SALSIFY_AVAILABLE, false);
                commit(MUTATIONS.SET_OPTIMIZELY_AVAILABLE, false);
                commit(MUTATIONS.SET_YOUTUBE_AVAILABLE, false);
            }
        },

        async checkBynderPublishMode({ commit }) {
            try {
                const { data } = await FileBrowser.isPublishToBynder();
                commit(MUTATIONS.SET_IS_PUBLISH_TO_BYNDER, data.isPublishToBynder);
            } catch (error) {
                console.error('Error checking publish mode:', error);
            }
        },

        async updateSortOrder(_, { ids, parentId }) {
            await FileBrowser.updateSortOrder(ids, parentId);
        },
    }
}
